import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Skeleton,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { useAuth } from '../../../hooks/useAuth';
import {
    ModelTestResult,
    testModelConnectivity,
} from '../../../services/ai-admin-api';

const { Text } = Typography;

interface ModelEntry {
    id: string;
    label?: string;
    contextWindow?: number;
    pricePerMillionInputTokens?: number | null;
    pricePerMillionOutputTokens?: number | null;
    supportsTools?: boolean;
    supportsStreaming?: boolean;
}

interface ModelsProvidersPayload {
    schemaVersion: 1;
    systemSettingsKeys: {
        AVAILABLE_ANTHROPIC_MODELS?: string;
        AVAILABLE_OPENAI_MODELS?: string;
        AVAILABLE_GOOGLE_MODELS?: string;
        AVAILABLE_EMBEDDING_MODELS?: string;
        [k: string]: string | undefined;
    };
    configTableRows: Record<string, never>;
}

type TabKey = 'anthropic' | 'openai' | 'google' | 'embedding';

const TAB_DEFS: Array<{ key: TabKey; settingKey: string; label: string }> = [
    { key: 'anthropic', settingKey: 'AVAILABLE_ANTHROPIC_MODELS', label: 'Anthropic' },
    { key: 'openai', settingKey: 'AVAILABLE_OPENAI_MODELS', label: 'OpenAI' },
    { key: 'google', settingKey: 'AVAILABLE_GOOGLE_MODELS', label: 'Google' },
    { key: 'embedding', settingKey: 'AVAILABLE_EMBEDDING_MODELS', label: 'Embedding' },
];

function parseModels(json: string | undefined): ModelEntry[] {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? (parsed as ModelEntry[]) : [];
    } catch {
        return [];
    }
}

/**
 * Configure tab body for the Models & Providers subsystem.
 *
 * Three tabs (Anthropic / OpenAI / Embedding). Each tab shows a table of
 * model entries with editable id, label, contextWindow, pricing, and tags;
 * add/remove rows. Pricing is USD per million tokens; null displays as
 * "unknown" and the sandbox cost estimator handles that gracefully.
 *
 * Adding/removing/editing a model is a versioned change — admins can publish
 * a new model entry without redeploying the API as soon as a new release
 * lands.
 */
export const ModelsProvidersConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<ModelsProvidersPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [activeTab, setActiveTab] = useState<TabKey>('anthropic');
    const [editing, setEditing] = useState<{ tabKey: string; index: number; entry: ModelEntry } | null>(null);
    const [testInFlight, setTestInFlight] = useState<Record<string, boolean>>({});
    const [testResults, setTestResults] = useState<Record<string, ModelTestResult>>({});

    const testKey = (tabKey: string, modelId: string) => `${tabKey}:${modelId}`;

    const runTest = async (tabKey: string, modelId: string) => {
        const key = testKey(tabKey, modelId);
        if (!modelId) return;
        setTestInFlight((s) => ({ ...s, [key]: true }));
        try {
            const result = await testModelConnectivity(jwt, {
                kind: tabKey === 'embedding' ? 'embedding' : 'chat',
                provider:
                    tabKey === 'embedding'
                        ? 'openai'
                        : (tabKey as 'anthropic' | 'openai' | 'google'),
                modelId,
            });
            setTestResults((s) => ({ ...s, [key]: result }));
        } catch (err: any) {
            setTestResults((s) => ({
                ...s,
                [key]: {
                    ok: false,
                    kind: tabKey === 'embedding' ? 'embedding' : 'chat',
                    provider: tabKey === 'embedding' ? 'openai' : tabKey,
                    modelId,
                    error: err.message || 'Test failed',
                },
            }));
        } finally {
            setTestInFlight((s) => ({ ...s, [key]: false }));
        }
    };

    const modelsByTab = useMemo(() => {
        const out: Record<string, ModelEntry[]> = {};
        for (const def of TAB_DEFS) {
            out[def.key] = parseModels(editor.payload?.systemSettingsKeys?.[def.settingKey]);
        }
        return out;
    }, [editor.payload]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    const setModelsForTab = (tabKey: string, next: ModelEntry[]) => {
        const def = TAB_DEFS.find((d) => d.key === tabKey)!;
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: {
                ...prev!.systemSettingsKeys,
                [def.settingKey]: JSON.stringify(next, null, 2),
            },
        }));
    };

    const handleAdd = () => {
        setEditing({
            tabKey: activeTab,
            index: -1,
            entry: {
                id: '',
                label: '',
                contextWindow: undefined,
                pricePerMillionInputTokens: null,
                pricePerMillionOutputTokens: null,
                supportsTools: true,
                supportsStreaming: true,
            },
        });
    };

    const handleEdit = (tabKey: string, index: number) => {
        const list = modelsByTab[tabKey];
        if (!list || !list[index]) return;
        setEditing({ tabKey, index, entry: { ...list[index] } });
    };

    const handleDelete = (tabKey: string, index: number) => {
        Modal.confirm({
            title: 'Remove this model?',
            content: 'It will no longer be selectable as a primary or fallback model. Saved drafts referencing it will still work.',
            okText: 'Remove',
            okButtonProps: { danger: true },
            onOk: () => {
                const next = [...modelsByTab[tabKey]];
                next.splice(index, 1);
                setModelsForTab(tabKey, next);
            },
        });
    };

    const handleSaveEntry = () => {
        if (!editing) return;
        if (!editing.entry.id?.trim()) {
            message.error('Model id is required.');
            return;
        }
        const list = [...modelsByTab[editing.tabKey]];
        if (editing.index === -1) {
            if (list.some((m) => m.id === editing.entry.id)) {
                message.error('A model with that id already exists.');
                return;
            }
            list.push(editing.entry);
        } else {
            list[editing.index] = editing.entry;
        }
        setModelsForTab(editing.tabKey, list);
        setEditing(null);
    };

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Available models for every AI subsystem. Adding or pricing a new model is a
                    versioned change — no API redeploy needed.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                message="Pricing is USD per million tokens"
                description="Leave price fields blank/null when pricing is uncertain — the sandbox cost estimator (Phase 4) treats null as 'cost unknown' rather than refusing to run."
            />

            <Tabs
                activeKey={activeTab}
                onChange={(k) => setActiveTab(k as typeof activeTab)}
                items={TAB_DEFS.map((def) => ({
                    key: def.key,
                    label: def.label,
                    children: (
                        <ModelsTable
                            models={modelsByTab[def.key]}
                            tabKey={def.key}
                            isEmbedding={def.key === 'embedding'}
                            onEdit={(i) => handleEdit(def.key, i)}
                            onDelete={(i) => handleDelete(def.key, i)}
                            onAdd={handleAdd}
                            onTest={(modelId) => runTest(def.key, modelId)}
                            testInFlight={testInFlight}
                            testResults={testResults}
                            testKeyOf={(modelId) => testKey(def.key, modelId)}
                        />
                    ),
                }))}
            />

            <ModelEditModal
                editing={editing}
                onCancel={() => setEditing(null)}
                onSave={handleSaveEntry}
                onChange={(entry) => setEditing((prev) => (prev ? { ...prev, entry } : prev))}
            />
        </div>
    );
};

const ModelsTable: React.FC<{
    models: ModelEntry[];
    tabKey: string;
    isEmbedding: boolean;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
    onAdd: () => void;
    onTest: (modelId: string) => void;
    testInFlight: Record<string, boolean>;
    testResults: Record<string, ModelTestResult>;
    testKeyOf: (modelId: string) => string;
}> = ({
    models,
    tabKey,
    isEmbedding,
    onEdit,
    onDelete,
    onAdd,
    onTest,
    testInFlight,
    testResults,
    testKeyOf,
}) => {
    return (
        <>
            <Space style={{ marginBottom: 8 }}>
                <Button icon={<PlusOutlined />} onClick={onAdd}>
                    Add model
                </Button>
            </Space>
            {models.length === 0 ? (
                <Empty description="No models configured for this provider." />
            ) : (
                <Table<ModelEntry & { __idx: number }>
                    size="small"
                    rowKey={(row) => `${tabKey}:${row.id}:${row.__idx}`}
                    pagination={false}
                    dataSource={models.map((m, i) => ({ ...m, __idx: i }))}
                    columns={[
                        {
                            title: 'ID',
                            dataIndex: 'id',
                            render: (id: string) => <code>{id}</code>,
                        },
                        {
                            title: 'Label',
                            dataIndex: 'label',
                        },
                        {
                            title: 'Context',
                            dataIndex: 'contextWindow',
                            width: 100,
                            render: (v: number | undefined) =>
                                v ? `${(v / 1000).toFixed(0)}k` : '—',
                        },
                        {
                            title: 'Input $/M',
                            dataIndex: 'pricePerMillionInputTokens',
                            width: 100,
                            render: (v: number | null | undefined) =>
                                v === null || v === undefined ? (
                                    <Text type="secondary">unknown</Text>
                                ) : (
                                    `$${v}`
                                ),
                        },
                        {
                            title: 'Output $/M',
                            dataIndex: 'pricePerMillionOutputTokens',
                            width: 110,
                            render: (v: number | null | undefined) =>
                                v === null || v === undefined ? (
                                    <Text type="secondary">unknown</Text>
                                ) : (
                                    `$${v}`
                                ),
                        },
                        {
                            title: 'Tags',
                            key: 'tags',
                            width: 180,
                            render: (_v, row) => (
                                <Space size={4} wrap>
                                    {!isEmbedding && row.supportsTools && <Tag>tools</Tag>}
                                    {!isEmbedding && row.supportsStreaming && <Tag>streaming</Tag>}
                                    {isEmbedding && <Tag>embedding</Tag>}
                                </Space>
                            ),
                        },
                        {
                            title: 'Test',
                            key: 'test',
                            width: 220,
                            render: (_v, row) => {
                                const key = testKeyOf(row.id);
                                const result = testResults[key];
                                const inFlight = testInFlight[key];
                                return (
                                    <Space size={4} wrap>
                                        <Tooltip title="Send a tiny ping to verify this model id and API key.">
                                            <Button
                                                size="small"
                                                icon={<ThunderboltOutlined />}
                                                loading={inFlight}
                                                onClick={() => onTest(row.id)}
                                                disabled={!row.id}
                                            >
                                                Test
                                            </Button>
                                        </Tooltip>
                                        {result?.ok && (
                                            <Tooltip title={result.detail}>
                                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                                    OK ({result.latencyMs ?? '?'}ms)
                                                </Tag>
                                            </Tooltip>
                                        )}
                                        {result && !result.ok && (
                                            <Tooltip title={result.error}>
                                                <Tag color="red" icon={<CloseCircleOutlined />}>
                                                    Failed
                                                </Tag>
                                            </Tooltip>
                                        )}
                                    </Space>
                                );
                            },
                        },
                        {
                            title: 'Actions',
                            key: 'actions',
                            width: 120,
                            render: (_v, row) => (
                                <Space>
                                    <Tooltip title="Edit">
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => onEdit(row.__idx)}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => onDelete(row.__idx)}
                                        />
                                    </Tooltip>
                                </Space>
                            ),
                        },
                    ]}
                />
            )}
        </>
    );
};

const ModelEditModal: React.FC<{
    editing: { tabKey: string; index: number; entry: ModelEntry } | null;
    onCancel: () => void;
    onSave: () => void;
    onChange: (entry: ModelEntry) => void;
}> = ({ editing, onCancel, onSave, onChange }) => {
    if (!editing) return null;
    const isEmbedding = editing.tabKey === 'embedding';
    const isNew = editing.index === -1;
    return (
        <Modal
            open
            title={isNew ? `Add ${editing.tabKey} model` : `Edit ${editing.entry.id}`}
            onCancel={onCancel}
            onOk={onSave}
            okText="Apply (still need to Save draft + Publish)"
            width={640}
        >
            <Form layout="vertical">
                <Form.Item label="Model ID" required>
                    <Input
                        value={editing.entry.id}
                        onChange={(e) => onChange({ ...editing.entry, id: e.target.value })}
                        placeholder={
                            isEmbedding
                                ? 'text-embedding-3-large'
                                : editing.tabKey === 'google'
                                    ? 'gemini-2.5-flash'
                                    : editing.tabKey === 'openai'
                                        ? 'gpt-4o'
                                        : 'claude-sonnet-4-6'
                        }
                        disabled={!isNew}
                    />
                    {!isNew && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            ID is the runtime key — changing it would break references in saved
                            drafts. Remove + re-add to rename.
                        </Text>
                    )}
                </Form.Item>
                <Form.Item label="Display name">
                    <Input
                        value={editing.entry.label ?? ''}
                        onChange={(e) => onChange({ ...editing.entry, label: e.target.value })}
                        placeholder="Claude Sonnet 4.6"
                    />
                </Form.Item>
                <Form.Item label="Context window (tokens)">
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        value={editing.entry.contextWindow ?? null}
                        onChange={(v) =>
                            onChange({ ...editing.entry, contextWindow: v ?? undefined })
                        }
                        placeholder="200000"
                    />
                </Form.Item>
                <Space style={{ width: '100%' }}>
                    <Form.Item label="Input $/M tokens" style={{ flex: 1 }}>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={0.01}
                            value={editing.entry.pricePerMillionInputTokens ?? null}
                            onChange={(v) =>
                                onChange({
                                    ...editing.entry,
                                    pricePerMillionInputTokens: v === null ? null : Number(v),
                                })
                            }
                            placeholder="3"
                        />
                    </Form.Item>
                    {!isEmbedding && (
                        <Form.Item label="Output $/M tokens" style={{ flex: 1 }}>
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                value={editing.entry.pricePerMillionOutputTokens ?? null}
                                onChange={(v) =>
                                    onChange({
                                        ...editing.entry,
                                        pricePerMillionOutputTokens: v === null ? null : Number(v),
                                    })
                                }
                                placeholder="15"
                            />
                        </Form.Item>
                    )}
                </Space>
                {!isEmbedding && (
                    <>
                        <Form.Item label="Supports tools">
                            <Switch
                                checked={editing.entry.supportsTools ?? false}
                                onChange={(v) =>
                                    onChange({ ...editing.entry, supportsTools: v })
                                }
                            />
                        </Form.Item>
                        <Form.Item label="Supports streaming">
                            <Switch
                                checked={editing.entry.supportsStreaming ?? false}
                                onChange={(v) =>
                                    onChange({ ...editing.entry, supportsStreaming: v })
                                }
                            />
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};
